/**
 * The queue and the online/offline state, as the two handles a screen needs.
 * `src/motion-react.ts` is the same idea for the motion pass.
 */

import { useCallback, useSyncExternalStore } from 'react'
import { summarise, type QueueSummary, type QueuedWrite } from './lib/queue'
import { discardQueuedWrite, isOnline, listQueuedWrites, retryQueuedWrite, subscribeQueue } from './queue-document'

export function useQueueSummary(): QueueSummary {
  const subscribe = useCallback((listener: () => void) => subscribeQueue(listener), [])
  const snapshot = useCallback(() => summarise(listQueuedWrites()), [])
  return useSyncExternalStore(subscribe, snapshot, () => ({ total: 0, waiting: 0, refused: 0 }))
}

export function useQueuedWrites(): readonly QueuedWrite[] {
  const subscribe = useCallback((listener: () => void) => subscribeQueue(listener), [])
  const snapshot = useCallback(() => listQueuedWrites(), [])
  return useSyncExternalStore(subscribe, snapshot, () => [])
}

/**
 * `navigator.onLine`, live. It is not asked before a write — a browser saying
 * "online" is not a promise the next request will succeed, only that it is
 * worth trying, which `src/queue.ts` already does on its own — it is only for
 * the banner's wording, which is allowed to be a little behind reality for a
 * moment.
 */
export function useOnline(): boolean {
  const subscribe = useCallback((listener: () => void) => {
    window.addEventListener('online', listener)
    window.addEventListener('offline', listener)
    return () => {
      window.removeEventListener('online', listener)
      window.removeEventListener('offline', listener)
    }
  }, [])
  return useSyncExternalStore(subscribe, isOnline, () => true)
}

export { discardQueuedWrite, retryQueuedWrite }
